import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Card } from '@/components/atoms/card';
import { cn } from '@/lib/utils';

/** Card matching the mockup `.card` (rounded-xl border bg-card, 20px padding). */
export function DetailCard({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  const hasHead = title != null || action != null;
  return (
    <Card className={cn('gap-0 p-5', className)}>
      {hasHead ? (
        <div className="flex min-h-7 items-center justify-between gap-3">
          {title != null ? (
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          ) : (
            <span />
          )}
          {action != null ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
      ) : null}
      <div className={cn(hasHead && 'mt-3', bodyClassName)}>{children}</div>
    </Card>
  );
}

export function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium">{value}</span>
    </div>
  );
}

export function ViewAllButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-semibold text-brand-blue hover:underline"
    >
      View all
    </button>
  );
}

export function AddButton({ onClick, label = 'Add' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-semibold text-brand-blue hover:underline"
    >
      + {label}
    </button>
  );
}

type AddRowProps =
  | {
      label?: string;
      onClick: () => void;
      link?: never;
    }
  | {
      label?: string;
      link: {
        to: '/dashboard/jobs/new' | '/dashboard/estimates/new';
        search: Record<string, unknown>;
      };
      onClick?: never;
    };

function AddRowContent({ label = 'Add' }: { label?: string }) {
  return (
    <>
      <span className="flex size-[22px] items-center justify-center rounded-md bg-muted">
        <Plus className="size-3.5" />
      </span>
      {label}
    </>
  );
}

/** Integrated "add" row at the foot of a list card (solid top border). */
export function AddRow(props: AddRowProps) {
  const className =
    'mt-2 flex w-full items-center gap-2 border-t border-border px-1 py-2.5 text-left text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground';

  if (props.link) {
    return (
      <Link
        to={props.link.to}
        search={props.link.search as never}
        preload="intent"
        className={className}
      >
        <AddRowContent label={props.label} />
      </Link>
    );
  }

  return (
    <button type="button" onClick={props.onClick} className={className}>
      <AddRowContent label={props.label} />
    </button>
  );
}
